<template>
  <div class="material-content">
    <!-- 描述与场景 -->
    <Divider title="描述与场景"></Divider>
    <el-tabs v-model="videoDescriptionActiveName" @tab-click="onVideoDescTabChange" v-if="form?.analysisTaskResult?.video_description">
      <el-tab-pane label="视频整体描述" name="0">
        <el-input :value="form.analysisTaskResult.video_description.overall_analysis_text" type="textarea" rows="10"></el-input>
      </el-tab-pane>
      <el-tab-pane
        v-for="(tab, index) in form.analysisTaskResult.video_description.timeline_events"
        :label="tab.time_segment"
        :key="index"
        :name="String(index + 1)"
      >
        <el-input :value="tab.timeline_events_text" type="textarea" rows="10"></el-input>
      </el-tab-pane>
    </el-tabs>
    <div v-else>暂无数据</div>

    <!-- AI生成标签 -->
    <Divider title="AI生成标签"></Divider>
    <div class="ai-tags">
      <el-tooltip
        v-for="(tag, index) in form.analysisTaskResult.suggested_tags"
        :key="index"
        effect="dark"
        :content="tag.name"
        placement="top"
      >
        <el-tag :closable="hasEditPermission" disable-transitions @close="onTagRemove(index)">
          {{ tag.lastTag }}
        </el-tag>
      </el-tooltip>

      <el-button v-if="hasEditPermission" type="text" @click="onAiTagAdd">+添加</el-button>
    </div>

    <!-- AI生成关键词 -->
    <Divider title="AI生成关键词"></Divider>
    <div class="ai-keywords" v-if="form?.analysisTaskResult?.keywords">
      <el-tag
        :closable="hasEditPermission"
        disable-transitions
        @close="onKeywordRemove(index)"
        v-for="(keyword, index) in form.analysisTaskResult.keywords"
        :key="index"
      >
        {{ keyword }}
      </el-tag>

      <template v-if="hasEditPermission">
        <el-input
          style="width: 80px"
          ref="keywordAddInputRef"
          v-if="aiKeywordAddInputVisible"
          v-model="keywordInputValue"
          @keyup.enter.native="onAddInputConfirm"
          @blur="onAddInputConfirm"
        >
        </el-input>
        <el-button v-else type="text" @click="showAddInput">+新增关键词</el-button>
      </template>
    </div>
    <div v-else>暂无关键词</div>

    <!-- 素材口播文案 -->
    <Divider title="素材口播文案"></Divider>
    <div style="height: 260px">
      <SubtitleDisplayForm
        v-model="subtitleList"
        :id="form?.detailImageId"
        @subtitle-save="onSubtitleSave"
        @subtitle-edit="onSubtitleEdit"
      ></SubtitleDisplayForm>
    </div>

    <!-- AI标签选择对话框 -->
    <el-dialog title="选择标签" :visible.sync="aiTagAddModalVisible" append-to-body>
      <el-cascader
        v-model="seletedAiTags"
        style="width: 100%; margin-bottom: 10px"
        :options="cascaderOpts"
        :props="cascaderProps"
        filterable
        collapse-tags
        placeholder="输入关键词搜索"
      ></el-cascader>

      <el-cascader-panel
        ref="aiTagCascaderRef"
        v-model="seletedAiTags"
        :props="cascaderProps"
        :options="cascaderOpts"
        @change="onAiTagSelectChange"
      >
      </el-cascader-panel>
      <div class="selected-tags">
        当前选择：
        <span v-for="(tag, index) in selectedNodes" :key="tag.id">
          <el-tooltip v-if="tag.desc" effect="dark" :content="tag.desc" placement="top">
            <span type="text"> <span v-if="index !== 0">、</span> {{ tag.label }} </span>
          </el-tooltip>
          <span v-else type="text"> <span v-if="index !== 0">、</span> {{ tag.label }} </span>
        </span>
      </div>
      <span slot="footer">
        <el-button @click="onAiTagModalClose">取 消</el-button>
        <el-button type="primary" :disabled="!selectedNodes.length" @click="onAitagModalConfirm">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import store from '@/store'
import { timeStringToSeconds } from '@/common/js/utils.js'
import Divider from '@/views/create/bytedance/components/layout/title-divider.vue'
import SubtitleDisplayForm from '@/views/material/library/components/material-model-subtitles.vue'
import { useAiKeywords, useAiTags, useSubtitles } from '../../hooks/useAuditForm.js'

// eslint-disable-next-line
const props = defineProps({
  form: {
    type: Object,
    default: () => {}
  }
})
const auditForm = ref({ ...props.form })
const accessAuth = computed(() => store.state.accessAuth)
const hasEditPermission = computed(() => accessAuth.value.includes('M21215'))
// eslint-disable-next-line
const emits = defineEmits(['subtitle-updated', 'update:modified', 'video-desc-tab-change'])

// 响应式数据
const videoDescriptionActiveName = ref('0')
const cascaderProps = { multiple: true, checkStrictly: true, emitPath: false, value: 'labelId', label: 'content' }

// 使用hooks
const { keywordAddInputRef, keywordInputValue, aiKeywordAddInputVisible, showAddInput, onAddInputConfirm, onKeywordRemove } = useAiKeywords(
  auditForm,
  emits
)

const {
  aiTagCascaderRef,
  cascaderOpts,
  aiTagAddModalVisible,
  seletedAiTags,
  selectedNodes,
  onAiTagAdd,
  onTagRemove,
  onAiTagModalClose,
  onAiTagSelectChange,
  onAitagModalConfirm
} = useAiTags(auditForm, emits)

const { subtitleList, onSubtitleEdit } = useSubtitles()

/**
 * @description: 切换视频描述文案tab时，视频定位到时间戳位置
 * @return {*}
 */
async function onVideoDescTabChange({ index }) {
  emits('video-desc-tab-change', index)
}

/**
 * @description: 字幕单独保存时，重新获取列表数据
 * @return {*}
 */
function onSubtitleSave() {
  emits('subtitle-updated')
}

// eslint-disable-next-line
defineExpose({
  subtitleList,
  initAuditFormSubtitles(subtitles) {
    const list = subtitles
      .split('\n')
      // 去掉前两项
      .slice(2)
      // 过滤空字符
      .filter((s) => !!s)

    const textlist = []
    for (let i = 0; i < list.length; i++) {
      const subtitle = list[i]
      const timerange = list[i - 1]

      // 每两组组成一个时间轴字幕结构
      if (i % 2 === 1) {
        textlist.push({ timerange, subtitle })
      }
    }
    subtitleList.value = textlist
  }
})
</script>

<style scoped lang="scss">
.material-content {
  .ai-tags,
  .ai-keywords {
    .el-tag {
      margin-bottom: 10px;
      margin-right: 8px;
    }

    .el-button {
      padding: 0;
    }
  }

  .selected-tags {
    font-size: 12px;
    margin-top: 10px;
  }
}
</style>
